import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { PermissionGate } from "../../components/PermissionGate";
import { StatusMessage } from "../../components/StatusMessage";
import { apiGet, apiSend } from "../../services/api";
import { useAuth } from "../../services/auth";
import type { Permission, UserProfile } from "../../types/api";
import "./users.css";

const PERMISSIONS: Array<{ value: Permission; label: string }> = [
  { value: "dashboard", label: "Indicadores" },
  { value: "graficos", label: "Análise Gráfica" },
  { value: "evasao", label: "Evasão" },
  { value: "questionario", label: "Questionários" },
  { value: "prova-facil", label: "Prova Fácil" },
  { value: "usuarios", label: "Usuários" }
];

type UserDraft = {
  email: string;
  name: string;
  userType: string;
  affiliation: string;
  permissions: Permission[];
};

type EditorState = {
  mode: "create" | "edit";
  originalEmail: string | null;
};

const EMPTY_DRAFT: UserDraft = {
  email: "",
  name: "",
  userType: "",
  affiliation: "",
  permissions: []
};

export function UsersPage() {
  const { token, profile, refreshSession } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [draft, setDraft] = useState<UserDraft>(EMPTY_DRAFT);
  const [deleteTarget, setDeleteTarget] = useState<UserProfile | null>(null);
  const [passwordResetTarget, setPasswordResetTarget] = useState<UserProfile | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError("");
    try {
      const payload = await apiGet<{ users: UserProfile[] }>("/api/admin/users", token);
      setUsers([...payload.users].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")));
      setLoaded(true);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Falha ao carregar usuários.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const userTypes = useMemo(() => [...new Set(users.map((user) => user.userType).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")), [users]);
  const filteredUsers = useMemo(() => {
    const normalizedQuery = normalize(query);
    return users.filter((user) => {
      if (typeFilter && user.userType !== typeFilter) return false;
      if (!normalizedQuery) return true;
      return [user.name, user.email, user.userType, user.affiliation || "", ...user.permissions]
        .some((value) => normalize(value).includes(normalizedQuery));
    });
  }, [query, typeFilter, users]);

  const adminCount = users.filter((user) => normalize(user.userType) === "admin").length;
  const affiliationCount = new Set(users.map((user) => user.affiliation).filter(Boolean)).size;
  const permissionCount = users.reduce((sum, user) => sum + user.permissions.length, 0);

  function openCreate() {
    setDraft(EMPTY_DRAFT);
    setEditor({ mode: "create", originalEmail: null });
    setActionError("");
    setNotice("");
  }

  function openEdit(user: UserProfile) {
    setDraft({
      email: user.email,
      name: user.name,
      userType: user.userType,
      affiliation: user.affiliation || "",
      permissions: [...user.permissions]
    });
    setEditor({ mode: "edit", originalEmail: user.email });
    setActionError("");
    setNotice("");
  }

  function closeEditor() {
    if (saving) return;
    setEditor(null);
    setDraft(EMPTY_DRAFT);
    setActionError("");
  }

  function togglePermission(permission: Permission) {
    setDraft((current) => ({
      ...current,
      permissions: current.permissions.includes(permission)
        ? current.permissions.filter((item) => item !== permission)
        : [...current.permissions, permission]
    }));
  }

  async function saveUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !editor) return;
    if (!draft.permissions.length) {
      setActionError("Selecione ao menos uma permissão.");
      return;
    }

    setSaving(true);
    setActionError("");
    setNotice("");
    const body: Record<string, unknown> = {
      email: draft.email.trim().toLowerCase(),
      name: draft.name.trim(),
      userType: draft.userType.trim(),
      affiliation: draft.affiliation.trim() || null,
      permissions: draft.permissions
    };

    try {
      const path = editor.mode === "create"
        ? "/api/admin/users"
        : `/api/admin/users/${encodeURIComponent(editor.originalEmail || draft.email)}`;
      const method = editor.mode === "create" ? "POST" : "PATCH";
      await apiSend<{ user: UserProfile }, Record<string, unknown>>(path, method, body, token);
      const editedCurrentUser = normalize(editor.originalEmail || draft.email) === normalize(profile?.email || "");
      setEditor(null);
      setDraft(EMPTY_DRAFT);
      setNotice(editor.mode === "create" ? "Usuário cadastrado com sucesso." : "Usuário atualizado com sucesso.");
      await loadUsers();
      if (editedCurrentUser) await refreshSession();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Falha ao salvar usuário.");
    } finally {
      setSaving(false);
    }
  }

  async function deactivateUser() {
    if (!token || !deleteTarget) return;
    setSaving(true);
    setActionError("");
    setNotice("");
    try {
      await apiSend<{ email: string }, Record<string, unknown>>(`/api/admin/users/${encodeURIComponent(deleteTarget.email)}`, "DELETE", null, token);
      setDeleteTarget(null);
      setNotice("Usuário desativado com sucesso.");
      await loadUsers();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Falha ao desativar usuário.");
    } finally {
      setSaving(false);
    }
  }

  async function requestPasswordReset() {
    if (!token || !passwordResetTarget) return;
    setResettingPassword(true);
    setActionError("");
    setNotice("");
    try {
      const target = passwordResetTarget;
      await apiSend<{ email: string }, Record<string, unknown>>(
        `/api/admin/users/${encodeURIComponent(target.email)}/password-reset`,
        "POST",
        null,
        token
      );
      setPasswordResetTarget(null);
      setNotice(`Solicitação de redefinição registrada para ${target.email}. Se a conta Firebase já existir, o usuário receberá o e-mail.`);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Falha ao solicitar redefinição de senha.");
    } finally {
      setResettingPassword(false);
    }
  }

  return (
    <PermissionGate permission="usuarios">
      <div className="users-page">
        <header className="users-header">
          <div>
            <p className="eyebrow">Administração de acesso</p>
            <h1>Usuários e permissões</h1>
            <p>Gerencie os perfis autorizados e o acesso individual aos módulos do painel.</p>
          </div>
          <div className="users-header__actions">
            <button type="button" className="button button--secondary" onClick={() => void loadUsers()} disabled={loading}>Atualizar</button>
            <button type="button" className="button button--primary" onClick={openCreate}>Novo usuário</button>
          </div>
        </header>

        {loadError ? (
          <StatusMessage tone="danger" title="Não foi possível acessar os usuários">
            <p>{friendlyLoadError(loadError)}</p>
            <button type="button" className="users-link-button" onClick={() => void loadUsers()}>Tentar novamente</button>
          </StatusMessage>
        ) : null}
        {actionError ? <StatusMessage tone="danger" title="Operação não concluída">{actionError}</StatusMessage> : null}
        {notice ? <StatusMessage tone="success">{notice}</StatusMessage> : null}

        {loaded ? (
          <section className="users-metrics" aria-label="Resumo dos usuários ativos">
            <UserMetric label="Usuários ativos" value={users.length} detail="Base de perfis ativos" help="Cálculo: conta os perfis ativos retornados pelo D1. Perfis desativados não entram na base; por ser uma contagem, não há denominador." />
            <UserMetric label="Administradores" value={adminCount} detail={`${formatShare(adminCount, users.length)} dos usuários ativos`} help="Cálculo: perfis com tipo “Admin” ÷ total de usuários ativos × 100. O total de usuários ativos é o denominador." />
            <UserMetric label="Vínculos cadastrados" value={affiliationCount} detail="Nomes de vínculo distintos" help="Cálculo: conta uma vez cada vínculo preenchido entre os usuários ativos. Campos vazios não entram; este indicador não é percentual." />
            <UserMetric label="Acessos concedidos" value={permissionCount} detail={`${formatAverage(permissionCount, users.length)} por usuário, em média`} help="Cálculo: soma todas as permissões de módulo concedidas. A média exibida divide essa soma pelo total de usuários ativos." />
          </section>
        ) : null}

        {editor ? (
          <UserEditor
            editor={editor}
            draft={draft}
            saving={saving}
            onChange={setDraft}
            onTogglePermission={togglePermission}
            onCancel={closeEditor}
            onSubmit={saveUser}
          />
        ) : null}

        <section className="users-panel">
          <div className="users-panel__head">
            <div>
              <h2>Perfis ativos</h2>
              <p>{filteredUsers.length} de {users.length} usuários exibidos</p>
            </div>
            <div className="users-filters" aria-label="Filtros de usuários">
              <label>
                <span className="sr-only">Pesquisar usuários</span>
                <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Nome, e-mail ou vínculo" />
              </label>
              <label>
                <span className="sr-only">Filtrar por tipo</span>
                <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
                  <option value="">Todos os tipos</option>
                  {userTypes.map((type) => <option value={type} key={type}>{type}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="users-table-wrap">
            <table className="users-table">
              <thead><tr><th>Usuário</th><th>Tipo</th><th>Vínculo</th><th>Permissões</th><th><span className="sr-only">Ações</span></th></tr></thead>
              <tbody>
                {loading && !users.length ? <LoadingRows /> : null}
                {!loading && !filteredUsers.length ? (
                  <tr><td colSpan={5} className="users-empty">{users.length ? "Nenhum usuário corresponde à pesquisa." : "Nenhum usuário ativo cadastrado."}</td></tr>
                ) : null}
                {filteredUsers.map((user) => {
                  const isCurrentUser = normalize(user.email) === normalize(profile?.email || "");
                  return (
                    <tr key={user.email}>
                      <td><div className="users-identity"><span aria-hidden="true">{initials(user.name)}</span><div><strong>{user.name}</strong><small>{user.email}{isCurrentUser ? " · você" : ""}</small></div></div></td>
                      <td><span className="users-type">{user.userType}</span></td>
                      <td>{user.affiliation || <span className="users-muted">Não informado</span>}</td>
                      <td><div className="users-permissions">{user.permissions.map((permission) => <span key={permission}>{permissionLabel(permission)}</span>)}</div></td>
                      <td><div className="users-row-actions"><button type="button" onClick={() => openEdit(user)} aria-label={`Editar ${user.name}`}>Editar</button><button type="button" className="users-password-reset" onClick={() => { setPasswordResetTarget(user); setActionError(""); setNotice(""); }} aria-label={`Redefinir senha de ${user.name}`}>Redefinir senha</button><button type="button" className="users-delete" onClick={() => setDeleteTarget(user)} disabled={isCurrentUser} title={isCurrentUser ? "O perfil em uso não pode ser desativado nesta sessão." : `Desativar ${user.name}`}>Desativar</button></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {deleteTarget ? (
          <div className="users-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setDeleteTarget(null); }}>
            <section className="users-dialog" role="alertdialog" aria-modal="true" aria-labelledby="deactivate-user-title">
              <span className="users-dialog__icon" aria-hidden="true">!</span>
              <h2 id="deactivate-user-title">Desativar usuário?</h2>
              <p><strong>{deleteTarget.name}</strong> perderá o acesso ao painel. O histórico de auditoria será preservado.</p>
              <div><button type="button" className="button button--secondary" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancelar</button><button type="button" className="button users-danger-button" onClick={() => void deactivateUser()} disabled={saving}>{saving ? "Desativando..." : "Desativar"}</button></div>
            </section>
          </div>
        ) : null}

        {passwordResetTarget ? (
          <div className="users-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !resettingPassword) setPasswordResetTarget(null); }}>
            <section className="users-dialog" role="alertdialog" aria-modal="true" aria-labelledby="reset-password-title">
              <span className="users-dialog__icon users-dialog__icon--password" aria-hidden="true">@</span>
              <h2 id="reset-password-title">Enviar redefinição de senha?</h2>
              <p>O Firebase enviará as instruções para <strong>{passwordResetTarget.email}</strong>. O usuário definirá a própria senha; nenhuma senha será exibida ou armazenada no painel.</p>
              <div><button type="button" className="button button--secondary" onClick={() => setPasswordResetTarget(null)} disabled={resettingPassword}>Cancelar</button><button type="button" className="button button--primary" onClick={() => void requestPasswordReset()} disabled={resettingPassword}>{resettingPassword ? "Enviando..." : "Enviar redefinição"}</button></div>
            </section>
          </div>
        ) : null}
      </div>
    </PermissionGate>
  );
}

function UserEditor({ editor, draft, saving, onChange, onTogglePermission, onCancel, onSubmit }: {
  editor: EditorState;
  draft: UserDraft;
  saving: boolean;
  onChange: (draft: UserDraft) => void;
  onTogglePermission: (permission: Permission) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <section className="users-editor" aria-labelledby="user-editor-title">
      <div className="users-editor__head"><div><p className="eyebrow">{editor.mode === "create" ? "Novo acesso" : "Editar acesso"}</p><h2 id="user-editor-title">{editor.mode === "create" ? "Cadastrar usuário" : draft.name}</h2></div><button type="button" onClick={onCancel} aria-label="Fechar formulário">Fechar</button></div>
      <form onSubmit={onSubmit}>
        <div className="users-form-grid">
          <label className="field"><span>Nome</span><input required value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} autoComplete="name" /></label>
          <label className="field"><span>E-mail institucional</span><input required type="email" value={draft.email} onChange={(event) => onChange({ ...draft, email: event.target.value })} disabled={editor.mode === "edit"} autoComplete="email" /></label>
          <label className="field"><span>Tipo de usuário</span><input required value={draft.userType} onChange={(event) => onChange({ ...draft, userType: event.target.value })} placeholder="Ex.: Setor, Polo ou Admin" /></label>
          <label className="field"><span>Vínculo</span><input value={draft.affiliation} onChange={(event) => onChange({ ...draft, affiliation: event.target.value })} placeholder="Opcional" /></label>
        </div>
        <fieldset className="users-permission-picker"><legend>Permissões de acesso</legend><p>Selecione ao menos um módulo.</p><div>{PERMISSIONS.map((permission) => <label key={permission.value}><input type="checkbox" checked={draft.permissions.includes(permission.value)} onChange={() => onTogglePermission(permission.value)} /><span>{permission.label}</span></label>)}</div></fieldset>
        <div className="users-editor__actions"><button type="button" className="button button--secondary" onClick={onCancel} disabled={saving}>Cancelar</button><button type="submit" className="button button--primary" disabled={saving}>{saving ? "Salvando..." : editor.mode === "create" ? "Cadastrar usuário" : "Salvar alterações"}</button></div>
      </form>
    </section>
  );
}

function UserMetric({ label, value, detail, help }: { label: string; value: number; detail: string; help: string }) {
  const tooltipId = useId();
  return (
    <article>
      <div><span>{label}</span><button type="button" className="users-help" aria-label={`Como é calculado: ${label}`} aria-describedby={tooltipId}>i<span id={tooltipId} role="tooltip">{help}</span></button></div>
      <strong>{value.toLocaleString("pt-BR")}</strong>
      <small>{detail}</small>
    </article>
  );
}

function LoadingRows() {
  return <>{[0, 1, 2].map((index) => <tr className="users-loading-row" key={index}><td><span /></td><td><span /></td><td><span /></td><td><span /></td><td><span /></td></tr>)}</>;
}

function permissionLabel(permission: Permission): string {
  return PERMISSIONS.find((item) => item.value === permission)?.label || permission;
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase() || "?";
}

function normalize(value: string): string {
  return value.toLocaleLowerCase("pt-BR").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

function friendlyLoadError(message: string): string {
  if (/AUTH_DB|configurado/i.test(message)) return "A base de autenticação deste ambiente ainda não está vinculada. Configure o binding D1 AUTH_DB no projeto do Cloudflare Pages.";
  return message;
}

function formatShare(value: number, total: number): string {
  return `${(total ? (value / total) * 100 : 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

function formatAverage(value: number, total: number): string {
  return (total ? value / total : 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}
